/**
 * useNumerologyCalculatorViewModel
 *
 * Calcula o número de expressão do nome, grava o destino da Orbia no cookie `auth_redirect_to` e leva
 * à interpretação: logado navega; deslogado abre a modal de auth da página (`window.onOpenStyledModal`).
 */
import React, { useCallback, useState } from 'react';

import Cookie from 'js-cookie';
import { useIntl } from 'react-intl';

import {
    AUTH_REDIRECT_COOKIE,
    AUTH_REDIRECT_META,
    CLOSE_STYLED_MODAL_EVENT,
    MIN_LOADING_DURATION_MS,
    MIN_NAME_LENGTH,
    TNumerologyType
} from '../constants';

import Url from '../../../../../services/Url';
import Utils from '../../../../../services/Utils';
import { fetchNumerologyNumber } from '../services/numerologyCalculatorApi';

import { Steps } from '../../AuthFunctional/Constants';

interface IUseNumerologyCalculatorViewModelReturn {
    /** Nome digitado. */
    name: string;
    /** Mensagem de validação já traduzida; vazia quando o nome é válido. */
    error: string;
    /** `true` enquanto a API responde ou o tempo mínimo de loading não passou. */
    isLoading: boolean;
    /** Número de expressão; `null` antes do cálculo. */
    result: number | null;
    /** Atualiza o nome e limpa o erro quando ele volta a ser válido. */
    handleNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    /** Valida e calcula. */
    handleSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
    /** Leva à Orbia com a pergunta do resultado. */
    handleInterpretation: () => void;
    /** Volta ao formulário vazio. */
    handleNewCalculation: () => void;
}

const wait = (ms: number): Promise<void> => new Promise((resolve) => { setTimeout(resolve, ms); });

const openAuthRedirectingTo = (destination: string): void => {
    let meta = document.querySelector<HTMLMetaElement>(`meta[name="${AUTH_REDIRECT_META}"]`);
    const previousContent = meta ? meta.content : null;

    if (!meta) {
        meta = document.createElement('meta');
        meta.name = AUTH_REDIRECT_META;
        document.head.appendChild(meta);
    }

    meta.content = destination;

    const restore = (): void => {
        if (previousContent === null) {
            meta?.remove();
            return;
        }

        if (meta) meta.content = previousContent;
    };

    document.addEventListener(CLOSE_STYLED_MODAL_EVENT, restore, { once: true });

    window.onOpenStyledModal?.(Steps.BIRTH_DATA);
};

const isNameValid = (name: string): boolean => name.trim().length >= MIN_NAME_LENGTH;

const useNumerologyCalculatorViewModel = (type: TNumerologyType): IUseNumerologyCalculatorViewModelReturn => {
    const intl = useIntl();

    const [name, setName] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState<number | null>(null);
    const [orbiaPath, setOrbiaPath] = useState('');

    const buildOrbiaPath = useCallback((value: number): string => {
        const route = intl.formatMessage({ id: 'chatAI.assistantRoute' });
        const question = intl.formatMessage({ id: `numerology.types.${type}.question` }, { number: value });

        return `${route}?question=${encodeURIComponent(question)}`;
    }, [intl, type]);

    const calculate = useCallback(async (): Promise<void> => {
        setIsLoading(true);

        try {
            const [value] = await Promise.all([fetchNumerologyNumber(name.trim()), wait(MIN_LOADING_DURATION_MS)]);
            const path = buildOrbiaPath(value);

            setOrbiaPath(path);
            setResult(value);
        } catch (requestError) {
            console.error('Error calculating numerology:', requestError);
            setResult(null);
            setError(intl.formatMessage({ id: 'numerology.general.errors.request' }));
        } finally {
            setIsLoading(false);
        }
    }, [name, buildOrbiaPath, intl]);

    const handleNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        if (error && isNameValid(value)) setError('');

        setName(value);
    }, [error]);

    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (isLoading) return;

        if (!isNameValid(name)) {
            setError(intl.formatMessage({ id: 'numerology.general.errors.nameLength' }));
            return;
        }

        setError('');
        calculate();
    }, [isLoading, name, intl, calculate]);

    const handleInterpretation = useCallback(() => {
        const destination = Url.base(orbiaPath);

        Cookie.set(AUTH_REDIRECT_COOKIE, orbiaPath, { path: '/' });

        if (Utils.isLoggedIn() || typeof window.onOpenStyledModal !== 'function') {
            Url.location(destination);
            return;
        }

        openAuthRedirectingTo(destination);
    }, [orbiaPath]);

    const handleNewCalculation = useCallback(() => {
        setName('');
        setResult(null);
        setOrbiaPath('');
    }, []);

    return {
        name,
        error,
        isLoading,
        result,
        handleNameChange,
        handleSubmit,
        handleInterpretation,
        handleNewCalculation
    };
};

export default useNumerologyCalculatorViewModel;
